/**
 * useNotifications — provides the unread count (via TanStack Query so it
 * auto-refreshes on any ["notifications"] invalidation), latest-5 for the
 * header dropdown, and mutation helpers.
 *
 * Mount in Layout so it lives for the whole session. The key insight: the
 * unread count is a QUERY, not local state. Any component that calls
 * `qc.invalidateQueries({ queryKey: ["notifications"] })` (including the
 * Notifications page's own mutations) causes the count to re-fetch
 * automatically. No manual `setUnreadCount` bookkeeping needed.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { useOffline } from "@/offline/OfflineProvider";
import { readThrough } from "@/offline/read-through";
import type { ApiOk, ApiPaged, Notification } from "@/types/api";

interface UseNotificationsProps {
  userId: string;
}

export function useNotifications({ userId }: UseNotificationsProps) {
  const qc = useQueryClient();
  const { repo } = useOffline();

  // Unread count — a query so it auto-refreshes when ["notifications"] is
  // invalidated from anywhere (the page, the hook, the socket listener).
  // Local-first: online refreshes, offline serves the warmed count.
  const countQ = useQuery({
    queryKey: ["notifications", "unread-count"],
    queryFn: () =>
      readThrough(
        repo,
        "notifications:unread-count",
        () => api.get<ApiOk<{ count: number }>>("/api/notifications/unread-count").then((r) => r.data.count),
        async () => 0,
      ),
    enabled: !!userId,
    refetchInterval: 60_000,
  });

  // Latest 5 for the bell dropdown.
  const latestQ = useQuery({
    queryKey: ["notifications", "latest-5"],
    queryFn: () =>
      readThrough(
        repo,
        "notifications:latest-5",
        () => api.paged<Notification>("/api/notifications", { limit: 5 }),
        async (): Promise<ApiPaged<Notification>> => ({ success: true, data: [], meta: { page: 1, limit: 1, total: 0, totalPages: 1 } }),
      ),
    enabled: !!userId,
  });

  // ── Mutations (used by the header dropdown) ──

  const markReadMut = useMutation({
    mutationFn: (id: number) =>
      api.patch<ApiOk<Notification>>(`/api/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const markAllReadMut = useMutation({
    mutationFn: () => api.post<ApiOk<{ ok: boolean }>>("/api/notifications/read-all"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const deleteOneMut = useMutation({
    mutationFn: (id: number) =>
      api.delete<ApiOk<{ ok: boolean }>>(`/api/notifications/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const clearAllMut = useMutation({
    mutationFn: () =>
      api.delete<ApiOk<{ deleted: number }>>("/api/notifications/clear-all"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  return {
    unreadCount: countQ.data ?? 0,
    latest: latestQ.data?.data ?? [],
    latestLoading: latestQ.isLoading,

    markAsRead: (id: number) => markReadMut.mutate(id),
    markAllAsRead: () => markAllReadMut.mutate(),
    deleteOne: (id: number) => deleteOneMut.mutate(id),
    clearAll: () => clearAllMut.mutate(),

    markAllPending: markAllReadMut.isPending,
    clearAllPending: clearAllMut.isPending,
  };
}
